import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { locations, primaryLocation } from "@/lib/locations-data";
import { siteConfig } from "@/lib/site-config";

/** Brand colors, matching --primary and --secondary in index.css. */
const NAVY = "hsl(212, 57%, 25%)";
const GOLD = "hsl(40, 52%, 52%)";

/** Start fetching Leaflet this far before the map scrolls into view. */
const PRELOAD_MARGIN = "400px";

const LocationMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const [activeSlug, setActiveSlug] = useState<string>(primaryLocation.slug);

  useEffect(() => {
    const initMap = async () => {
      // Leaflet and its stylesheet are ~150 KB of JS plus a stylesheet that
      // nothing above the fold needs, and the tile requests keep the network
      // busy well past first paint. Both are imported here rather than at
      // module scope so they land in their own chunk, fetched only once the
      // map is actually approaching the viewport.
      const [L] = await Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")]);

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
      }).setView([primaryLocation.lat, primaryLocation.lng], 10);

      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // 20-mile core service radius (32,187 meters)
      L.circle([primaryLocation.lat, primaryLocation.lng], {
        radius: 32187,
        color: NAVY,
        fillColor: NAVY,
        fillOpacity: 0.08,
        weight: 2,
        dashArray: "8 4",
      }).addTo(map);

      const primaryIcon = L.divIcon({
        className: "custom-marker-primary",
        html: `<div style="background:${GOLD};width:36px;height:36px;border-radius:50%;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
        </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });

      /**
       * Leaflet makes each marker a focusable role="button", so its box is a
       * touch target. A bare 20px dot is under the 24px minimum, so the dot is
       * centred inside a 24px transparent box: same size, same position on the
       * map, a target a finger can actually hit.
       */
      const dotIcon = (className: string, dot: string) =>
        L.divIcon({
          className,
          html: `<div style="width:24px;height:24px;display:flex;align-items:center;justify-content:center;">${dot}</div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });

      const cityIcon = dotIcon(
        "custom-marker-city",
        `<div style="background:${NAVY};width:20px;height:20px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div>`
      );

      const extendedIcon = dotIcon(
        "custom-marker-extended",
        `<div style="background:white;width:20px;height:20px;border-radius:50%;border:3px solid ${NAVY};box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div>`
      );

      locations.forEach((loc) => {
        const icon = loc.isPrimary ? primaryIcon : loc.extended ? extendedIcon : cityIcon;
        const badge = loc.isPrimary ? " · Main Office" : loc.extended ? " · Extended Area" : "";
        const marker = L.marker([loc.lat, loc.lng], { icon })
          .addTo(map)
          .bindPopup(
            `<div style="padding:8px;font-family:sans-serif;min-width:180px;">
              <strong style="font-size:14px;color:${NAVY};">${loc.name}, ${loc.state}</strong>
              <p style="color:#666;font-size:12px;margin:4px 0 6px;">${loc.region}${badge}</p>
              <a href="/locations/${loc.slug}" style="color:${NAVY};font-weight:600;font-size:12px;text-decoration:none;">View location →</a>
            </div>`
          );
        // Leaflet gives every marker role="button" and puts it in the tab
        // order, but a divIcon has no text, so screen readers announce nothing.
        marker.getElement()?.setAttribute("aria-label", `${loc.name}, ${loc.state} — view location`);
        markersRef.current[loc.slug] = marker;
      });
    };

    const container = mapRef.current;
    if (!container) return;

    let observer: IntersectionObserver | null = null;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer?.disconnect();
          observer = null;
          initMap();
        },
        { rootMargin: PRELOAD_MARGIN }
      );
      observer.observe(container);
    } else {
      initMap();
    }

    return () => {
      observer?.disconnect();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const handleLocationClick = (slug: string, lat: number, lng: number) => {
    setActiveSlug(slug);
    if (mapInstanceRef.current) {
      const zoom = slug === primaryLocation.slug ? 10 : 12;
      mapInstanceRef.current.flyTo([lat, lng], zoom, { duration: 1 });
      setTimeout(() => {
        markersRef.current[slug]?.openPopup();
      }, 800);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
      <div className="bg-primary px-5 py-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary-foreground" />
        <span className="font-heading font-bold text-primary-foreground text-sm tracking-wide">
          Service Area — Within {siteConfig.address.serviceRadius} of {siteConfig.address.city},{" "}
          {siteConfig.address.state}
        </span>
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-[35%] lg:border-r border-border overflow-y-auto max-h-[350px] lg:max-h-[520px] scrollbar-hide">
          {/* The row's "fly to on the map" button and its "View" link are two
              different destinations, so they are siblings rather than the link
              being nested inside the button. Nesting made the link a 43x16
              target overlapping a full-width one, and is invalid HTML. Each now
              carries the row's full vertical padding, so both are comfortably
              tappable and the row looks exactly as it did. */}
          {locations.map((loc) => (
            <div
              key={loc.slug}
              className={`w-full flex items-stretch justify-between border-b border-border transition-all ${
                activeSlug === loc.slug
                  ? "bg-primary/10 border-l-4 border-l-primary"
                  : "hover:bg-muted/50 border-l-4 border-l-transparent"
              }`}
            >
              <button
                type="button"
                onClick={() => handleLocationClick(loc.slug, loc.lat, loc.lng)}
                className="flex-1 min-w-0 text-left pl-5 pr-2 py-3 flex items-center gap-2.5"
              >
                {loc.isPrimary ? (
                  <span className="h-3 w-3 rounded-full bg-secondary shrink-0 ring-2 ring-secondary/30" />
                ) : (
                  <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
                )}
                <div>
                  <div className={`font-heading font-bold text-sm tracking-tight ${activeSlug === loc.slug ? "text-primary" : "text-foreground"}`}>
                    {loc.name}, {loc.state}
                  </div>
                  <div className="text-muted-foreground text-[11px]">
                    {loc.region}
                    {loc.isPrimary ? " · Main Office" : loc.extended ? " · Extended" : ""}
                  </div>
                </div>
              </button>
              <Link
                to={`/locations/${loc.slug}`}
                className="flex items-center pr-5 pl-2 py-3 text-xs font-semibold text-primary hover:text-primary/80 whitespace-nowrap"
              >
                View<span className="sr-only"> {loc.name}, {loc.state}</span> →
              </Link>
            </div>
          ))}
        </div>

        <div className="w-full lg:w-[65%] h-[400px] lg:h-[520px] relative" style={{ zIndex: 0 }}>
          <div ref={mapRef} className="w-full h-full" style={{ zIndex: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default LocationMap;
